'use client';

import React, { useMemo } from 'react';
import type { SectionProps } from '@/lib/storefront/section-templates';
import { containerClass, sectionStyle } from '@/lib/storefront/section-style';
import type { VariationDef } from '@/lib/storefront/section-library';
import { useStorefrontData } from '@/lib/storefront/storefront-data';

function buildSrcDoc(html: string, css: string, wrapperId: string): string {
  const scoped = css.trim();
  const safeWrapper = wrapperId.replace(/[^a-zA-Z0-9_-]/g, '') || 'section';
  const body = html.trim() || '<p style="color:#64748b;font-family:system-ui;padding:1.5rem;">Empty custom section</p>';
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/><base target="_blank" rel="noopener"/><style>html,body{margin:0;padding:0;font-family:system-ui,sans-serif;}img{max-width:100%;height:auto;}${scoped}</style></head><body><div class="sb-custom-${safeWrapper}" id="sb-root">${body}</div></body></html>`;
}

/** Renders imported HTML in a sandboxed iframe (no scripts). */
export function CustomHtmlSection({ content, style }: SectionProps) {
  const { preview } = useStorefrontData();
  const name = typeof content.name === 'string' ? content.name : 'Custom section';
  const html = typeof content.customHtml === 'string' ? content.customHtml : '';
  const css = typeof content.scopedCss === 'string' ? content.scopedCss : '';
  const wrapperId = typeof content.wrapperId === 'string' && content.wrapperId
    ? content.wrapperId
    : 'section';
  const srcDoc = useMemo(() => buildSrcDoc(html, css, wrapperId), [html, css, wrapperId]);
  const warnings = Array.isArray(content.warnings)
    ? content.warnings.filter((item): item is string => typeof item === 'string')
    : [];

  return (
    <section className="w-full bg-white" style={sectionStyle(style)} data-custom-section={name}>
      <div className={containerClass(style)}>
        {preview && warnings.length > 0 && (
          <ul className="mb-3 list-disc space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-[11px] text-amber-900">
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        )}
        <iframe
          title={name}
          srcDoc={srcDoc}
          sandbox=""
          className="w-full min-h-[200px] rounded-xl border border-slate-200 bg-white"
          style={{ height: typeof content.frameHeight === 'number' ? content.frameHeight : 360 }}
        />
      </div>
    </section>
  );
}

export const CUSTOM_VARIATIONS: VariationDef[] = [
  {
    id: 'html',
    name: 'Imported HTML',
    description: 'A custom section created from HTML, CSS, or a ZIP package.',
    component: CustomHtmlSection,
    defaultContent: {
      name: 'Custom section',
      customHtml: '<div class="p-8 text-center"><h2>Your custom section</h2><p>Import HTML to replace this placeholder.</p></div>',
      scopedCss: '',
      wrapperId: 'placeholder',
      warnings: [],
      frameHeight: 280,
    },
    fields: [
      { key: 'name', label: 'Section name', type: 'text' },
      { key: 'frameHeight', label: 'Frame height (px)', type: 'number', min: 120, max: 1200 },
    ],
  },
];
